import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getTsWarehousingConsignmentsClient } from '@/api/warehousing-consignments.client';
import useGetPharmacistShipment from '@/hooks/useGetPharmacistShipment';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import type { GetConsignmentResponseSchema } from '@montugroup/contracts-warehousing-api';
import { toast } from '@montugroup/design-system';
import { useQueryClient } from '@tanstack/react-query';

export type PharmacistShipmentData = {
  shipment: GetConsignmentResponseSchema | null;
  loading: boolean;
  receivedQuantities: Record<string, number>;
  setReceivedQuantities: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  recipientName: string;
  setRecipientName: React.Dispatch<React.SetStateAction<string>>;
  signature: string | null;
  setSignature: React.Dispatch<React.SetStateAction<string | null>>;
  date: Date;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  submitAcknowledgement: (signatureDataUrl: string | null) => void;
  submitted: boolean;
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  disableForm: boolean;
  isError: boolean;
};

interface Props extends PropsWithChildren {
  id: string;
}

const client = getTsWarehousingConsignmentsClient();

export const PharmacistShipmentContext = createContext<PharmacistShipmentData>({
  shipment: null,
  loading: false,
  receivedQuantities: {},
  setReceivedQuantities: () => {},
  recipientName: '',
  setRecipientName: () => {},
  signature: null,
  setSignature: () => {},
  date: new Date(),
  setDate: () => {},
  submitAcknowledgement: () => {},
  submitted: false,
  setSubmitted: () => {},
  disableForm: false,
  isError: false,
});

export function PharmacistShipmentProvider(props: Props) {
  const { children, id } = props;

  const { data: shipmentData, isLoading: shipmentLoading, isError } = useGetPharmacistShipment(id);
  const { data: userData, isLoading: userLoading } = useUserProfileData();
  const queryClient = useQueryClient();

  const [receivedQuantities, setReceivedQuantities] = useState({} as Record<string, number>);
  const [recipientName, setRecipientName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  // get todays date
  const [date, setDate] = useState<Date>(new Date());
  const [submitted, setSubmitted] = useState(false);
  const { mutateAsync } = client.acknowledgeConsignment.useMutation({
    meta: {
      error: {
        showToast: true,
        log: 'Failed to acknowledge consignment',
        message: 'Something went wrong while acknowledging the shipment',
      },
    },
  });

  const shipment = useMemo(() => shipmentData?.body.data ?? null, [shipmentData]);
  const acknowledgement = useMemo(
    () => shipment?.acknowledgements?.[shipment.acknowledgements.length - 1] ?? null,
    [shipment],
  );
  const disableForm = useMemo(() => !!acknowledgement || submitted, [acknowledgement, submitted]);

  // Callback to submit the acknowledgement form information
  const submitAcknowledgement = useCallback(
    async (signatureDataUrl: string | null) => {
      setSubmitted(true);

      const items = shipment?.items.map((item) => ({
        sku: item.productSku,
        quantity: receivedQuantities[item.id],
      }));

      const signatureContent = signatureDataUrl ?? signature;
      if (
        shipment &&
        signatureContent &&
        recipientName &&
        date &&
        items?.length === shipment?.items.length &&
        items?.every((item) => item.quantity >= 0)
      ) {
        try {
          const result = await mutateAsync({
            body: {
              data: {
                recipientName,
                signatureContent,
                version: new Date(shipment.modifiedAt),
                items: shipment?.items.map((item) => ({
                  sku: item.productSku,
                  quantity: receivedQuantities[item.id],
                })),
              },
            },
            params: { consignmentId: shipment.id },
            headers: { authorization: getAuthBearerToken() },
          });

          if (result.status === 204) {
            toast.success('Shipment acknowledged successfully', { autoClose: 3000 });
            queryClient.invalidateQueries({ queryKey: [shipment.id] });
          } else {
            setSubmitted(false);
          }
        } catch {
          setSubmitted(false);
        }
      } else {
        toast.error('Acknowledgment inputs are invalid', { autoClose: 3000 });
        setSubmitted(false);
      }
    },
    [recipientName, date, receivedQuantities, shipment, signature],
  );

  // update form if their is a previous acknowledgement
  useEffect(() => {
    if (!shipment) {
      return;
    }
    setReceivedQuantities(
      shipment.items.reduce(
        (acc, item) => {
          acc[item.id] = item.quantityReceived ?? item.quantity ?? 0;
          return acc;
        },
        {} as Record<string, number>,
      ),
    );

    setSignature(acknowledgement?.signaturePath ?? null);
    setDate(new Date(acknowledgement?.createdDate ?? Date.now()));
  }, [shipment, shipmentLoading, acknowledgement]);

  // set recipient name to pharmacists name if no acknowledgement, and not already input
  useEffect(() => {
    if (userLoading || shipmentLoading || recipientName?.length) {
      return;
    }

    setRecipientName(
      acknowledgement
        ? acknowledgement.recipientName
        : userData
          ? `${userData?.user.first_name} ${userData?.user.last_name}`
          : '',
    );
  }, [userData, userLoading, shipmentLoading, acknowledgement]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      shipment,
      loading: shipmentLoading,
      receivedQuantities,
      setReceivedQuantities,
      recipientName,
      setRecipientName,
      signature,
      setSignature,
      date,
      setDate,
      submitAcknowledgement,
      submitted,
      setSubmitted,
      disableForm,
      isError,
    }),
    [
      shipment,
      shipmentLoading,
      receivedQuantities,
      setReceivedQuantities,
      recipientName,
      setRecipientName,
      signature,
      setSignature,
      date,
      setDate,
      submitAcknowledgement,
      submitted,
      setSubmitted,
      disableForm,
      isError,
    ],
  );

  return <PharmacistShipmentContext.Provider value={value}>{children}</PharmacistShipmentContext.Provider>;
}

export const usePharmacistShipment = () => {
  const {
    shipment,
    loading,
    receivedQuantities,
    setReceivedQuantities,
    recipientName,
    setRecipientName,
    signature,
    setSignature,
    date,
    setDate,
    submitAcknowledgement,
    submitted,
    setSubmitted,
    disableForm,
    isError,
  } = useContext(PharmacistShipmentContext);

  return {
    shipment,
    loading,
    receivedQuantities,
    setReceivedQuantities,
    recipientName,
    setRecipientName,
    signature,
    setSignature,
    date,
    setDate,
    submitAcknowledgement,
    submitted,
    setSubmitted,
    disableForm,
    isError,
  };
};
